import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { testimonialsApi } from '../../../api/adminApi';
import './TestimonialView.css';

function TestimonialView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    testimonialsApi
      .getOne(id)
      .then((data) => setTestimonial(data))
      .catch((err) => setError(err.userMessage || 'Failed to load testimonial'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return isNaN(d.getTime())
        ? str
        : d.toLocaleDateString('en-US', { dateStyle: 'medium' }) +
            ' ' +
            d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return str;
    }
  };

  const renderStars = (rating) => {
    const r = Math.min(5, Math.max(0, Number(rating) || 0));
    return (
      <>
        <span className="tv-stars-filled">{'★'.repeat(r)}</span>
        <span className="tv-stars-empty">{'★'.repeat(5 - r)}</span>
        <span className="tv-stars-label">{r} / 5</span>
      </>
    );
  };

  if (loading) {
    return <div className="tv-loading">Loading testimonial…</div>;
  }

  if (error || !testimonial) {
    return (
      <div className="tv-wrap">
        <div className="tv-header">
          <Link to="/admin/testimonials" className="tv-back">← Testimonials</Link>
        </div>
        <div className="tv-error" role="alert">{error || 'Testimonial not found.'}</div>
      </div>
    );
  }

  return (
    <div className="tv-wrap">
      {/* ── Header ── */}
      <div className="tv-header">
        <Link to="/admin/testimonials" className="tv-back">← Testimonials</Link>
        <h2 className="tv-title">{testimonial.name || '—'}</h2>
        <div className="tv-header-actions">
          <button
            type="button"
            className="tv-btn tv-btn-edit"
            onClick={() =>
              navigate('/admin/testimonials', { state: { editTestimonial: testimonial } })
            }
          >
            Edit
          </button>
        </div>
      </div>

      <div className="tv-card">
        {/* ── Media row ── */}
        {(testimonial.avatar_url || testimonial.photo_url || testimonial.video_url) && (
          <div className="tv-media-row">
            {testimonial.avatar_url && (
              <div className="tv-media-block">
                <span className="tv-media-label">Avatar</span>
                <img src={testimonial.avatar_url} alt="Avatar" className="tv-avatar-img" />
              </div>
            )}
            {testimonial.photo_url && (
              <div className="tv-media-block">
                <span className="tv-media-label">Photo</span>
                <img src={testimonial.photo_url} alt="Photo" className="tv-photo-img" />
              </div>
            )}
            {testimonial.video_url && (
              <div className="tv-media-block tv-media-block--full">
                <span className="tv-media-label">Video</span>
                <video src={testimonial.video_url} controls className="tv-video" />
              </div>
            )}
          </div>
        )}

        {/* ── Details ── */}
        <dl className="tv-details">
          <div className="tv-row">
            <dt>Name</dt>
            <dd>{testimonial.name || '—'}</dd>
          </div>

          <div className="tv-row">
            <dt>Rating</dt>
            <dd className="tv-rating">{renderStars(testimonial.rating)}</dd>
          </div>

          <div className="tv-row">
            <dt>Review</dt>
            <dd className="tv-review">{testimonial.review || '—'}</dd>
          </div>

          <div className="tv-row">
            <dt>Featured</dt>
            <dd>
              <span className={`tv-badge ${testimonial.is_featured ? 'tv-badge-yes' : 'tv-badge-no'}`}>
                {testimonial.is_featured ? 'Yes' : 'No'}
              </span>
            </dd>
          </div>

          <div className="tv-row">
            <dt>Verified</dt>
            <dd>
              <span className={`tv-badge ${testimonial.is_verified ? 'tv-badge-yes' : 'tv-badge-no'}`}>
                {testimonial.is_verified ? 'Yes' : 'No'}
              </span>
            </dd>
          </div>

          {testimonial.creator && (
            <div className="tv-row">
              <dt>Created by</dt>
              <dd>
                <div>{testimonial.creator.name || '—'}</div>
                <div className="tv-sub">{testimonial.creator.email}</div>
              </dd>
            </div>
          )}

          <div className="tv-row">
            <dt>Created</dt>
            <dd>{formatDate(testimonial.created_at)}</dd>
          </div>

          <div className="tv-row">
            <dt>Updated</dt>
            <dd>{formatDate(testimonial.updated_at)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

export default TestimonialView;
